import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveProjectDetailsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveStrategicAxes,
} from "@planzone/view-models";

import { ZoneAvatar } from "@common/zoneAvatar";
import { zone as z } from "@planzone/dto";
import { TextInput } from "@common/textInput";
import { RichTextInput } from "@common/richTextInput";
import { ProjectPortfoliosList } from "./projectPortfoliosList";
import { ZoneSelection } from "./zoneSelection";
import { ProjectStatus } from "./projectStatus";
import { ProjectToolsConfiguration } from "./projectToolsConfiguration";
import { TagList } from "./tagList";
import { DateForm } from "./dateForm";
import { Props } from "./types";
import styles from "./styles.scss";
import { Safeguard } from "@scenes/safeguard";
import { useModal } from "@root/client/modal";
import { UseCases } from "@planzone/core";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { WeatherReport } from "@common/weatherReport";
import { WeightTag } from "@common/weightTag";
import { toFixed } from "@common/projectSection/utils";
import { ResourceList } from "@scenes/project/managerList";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { SecondaryCriteria } from "./secondaryCriteria";

function ProjectCreationForm({
  formData,
  zone,
  configuration,
  isMultiProject,
  template: projectTemplate,
  editForm,
  setConfiguration,
  selectZone,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const [titleError, setTitleError] = useState(false);
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const {
    template: projectTemplateSecondaryCriteria,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(
    projectTemplate ? projectTemplate.uuid : "",
    "project",
  );
  const template = [...planzoneTemplate, ...projectTemplateSecondaryCriteria];
  const {
    isRequestPending: isConfigurationRequestPending,
    isRequestSuccess: isConfigurationRequestSuccess,
    retrieveProjectDetailsConfiguration,
  } = useRetrieveProjectDetailsConfiguration(planzone);
  const { strategicAxes } = useRetrieveStrategicAxes();

  React.useEffect(() => {
    !isConfigurationRequestPending &&
      !isConfigurationRequestSuccess &&
      retrieveProjectDetailsConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    !!projectTemplate &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveSecondaryCriteriaTemplate({
        elementType: "project",
        namespace: projectTemplate.uuid,
        scope: "project",
      });
  }, [projectTemplate]);

  return (
    <div className={styles.projectCreationForm}>
      <div className={styles.column}>
        {!formData.isTemplate && (
          <div className={styles.formGroup}>
            <div className={styles.zoneTitle}>
              <span className={styles.inZoneSpace}>
                {t("projectCreation.inZone")}
              </span>
              {isMultiProject ? (
                <ZoneSelection
                  placeholder={t("projectCreation.zoneSelectionPlaceholder")}
                  selectedZone={zone || null}
                  selectZone={selectZone || (() => {})}
                />
              ) : (
                <React.Fragment>
                  <div className={styles.avatar}>
                    <ZoneAvatar {...(zone || { ...z, uuid: "" })} />
                  </div>
                  <span>{(zone || z).name}</span>
                </React.Fragment>
              )}
            </div>
          </div>
        )}
        <div className={styles.formGroup}>
          <h3 className={styles.title}>{t("projectCreation.nameTitle")}</h3>
          <TextInput
            placeholder={""}
            className={classNames(styles.nameInput, {
              [styles.errorTitleInput]: titleError,
            })}
            value={formData.name}
            input={(name) => editForm({ ...formData, name })}
            focus={() => setTitleError(false)}
            autoFocus
            blur={() =>
              !formData.name.length ? setTitleError(true) : setTitleError(false)
            }
          />
          {titleError && (
            <p className={styles.titleError}>
              {t("projectCreation.errorTitleInput")}
            </p>
          )}
        </div>
        <div className={styles.tagsGroup}>
          <div className={styles.projectPortfoliosList}>
            <h3 className={styles.title}>
              {t("projectCreation.projectPortfoliosList")}
            </h3>
            <ProjectPortfoliosList
              portfolios={formData.portfolios}
              removePortfolios={(portfolio) =>
                editForm({
                  ...formData,
                  portfolios: formData.portfolios.filter(
                    (p) => p.uuid !== portfolio.uuid,
                  ),
                })
              }
              addPortfolios={(portfolio) => {
                editForm({
                  ...formData,
                  portfolios: [...formData.portfolios, portfolio],
                });
              }}
            />
          </div>
          <div className={styles.tags}>
            <h3 className={styles.title}>{t("projectCreation.tagsTitle")}</h3>
            <TagList
              tags={formData.tags}
              assignTag={(tag) =>
                editForm({ ...formData, tags: [...formData.tags, tag] })
              }
              removeTag={(tag) =>
                editForm({
                  ...formData,
                  tags: formData.tags.filter((t) => t.name !== tag.name),
                })
              }
            />
          </div>
        </div>
        <div className={styles.formGroup}>
          <h3 className={styles.title}>
            {t("projectCreation.descriptionTitle")}
          </h3>
          <RichTextInput
            value={formData.description}
            maxLength={10000}
            placeholder={""}
            input={(description) => editForm({ ...formData, description })}
          />
        </div>
        <div className={styles.secondaryCriteria}>
          <div className={styles.heading}>
            <h3 className={styles.subTitle}>
              {t("projectDetails.secondaryCriteria")}
            </h3>
            <Safeguard useCases={[UseCases.editProjectDetailsConfiguration]}>
              {({ canEditProjectDetailsConfiguration }) =>
                canEditProjectDetailsConfiguration ? (
                  <div
                    className={styles.contextualMenuIcon}
                    onClick={() =>
                      pushView({
                        key: UseCases.retrieveProjectDetailsConfiguration,
                        data: projectTemplate,
                      })
                    }
                  >
                    <CogWheelIcon className={styles.configurationIcon} />
                  </div>
                ) : (
                  <div className={styles.configurationIcon} />
                )
              }
            </Safeguard>
          </div>
          <SecondaryCriteria
            project={formData}
            criteriaForms={template.reduce(
              (acc, criteria) => ({
                ...acc,
                [criteria.uuid]: (
                  <SecondaryCriteriaForm
                    criteria={criteria}
                    value={
                      formData.attributes
                        ? formData.attributes[criteria.uuid] || ""
                        : ""
                    }
                    flexible
                    disabled={false}
                    submit={(value) =>
                      editForm({
                        ...formData,
                        attributes: {
                          ...formData.attributes,
                          [criteria.uuid]: value,
                        },
                      })
                    }
                  />
                ),
              }),
              {
                zone: (
                  <div className={styles.dates}>
                    {formData.zone && (
                      <div className={styles.zone}>
                        <div className={styles.avatar}>
                          <ZoneAvatar
                            name={formData.zone.name}
                            color={formData.zone.color}
                          />
                        </div>
                        <p>{formData.zone.name}</p>
                      </div>
                    )}
                  </div>
                ),
                weather: (
                  <div className={styles.weather}>
                    <WeatherReport
                      weather={formData.attributes.weather}
                      className={styles.weatherIcon}
                    />
                  </div>
                ),
                strategicScore: (
                  <div className={styles.weather}>
                    <WeightTag
                      weight={formData.averageOfConsistencyOfStrategicAxes}
                      maxWeight={strategicAxes.reduce(
                        (acc, { weight }) => acc + weight * 2,
                        0,
                      )}
                    />
                  </div>
                ),
                activityMonitoring_done: (
                  <p className={classNames(styles.text, styles.positive)}>
                    {toFixed(formData.proportionOfAccomplishedWork)}%
                  </p>
                ),
                activityMonitoring_delayed: (
                  <p className={classNames(styles.text, styles.warning)}>
                    {toFixed(formData.proportionOfDelayedWork)}%
                  </p>
                ),
                activityMonitoring_opened: (
                  <p className={styles.text}>
                    {toFixed(formData.proportionOfOpenedWork)}%
                  </p>
                ),
                workload_allocatedTime: (
                  <p
                    className={classNames(styles.text, {
                      [styles.warning]:
                        formData.totalSumOfTimeAllocatedOnTheProject < 0,
                    })}
                  >
                    {toFixed(formData.totalSumOfTimeAllocatedOnTheProject)} h
                  </p>
                ),
                workload_spentTime: (
                  <p
                    className={classNames(styles.text, {
                      [styles.warning]: formData.totalOfTimesSpentInProject < 0,
                    })}
                  >
                    {toFixed(formData.totalOfTimesSpentInProject)} h
                  </p>
                ),
                workload_remainingWork: (
                  <p
                    className={classNames(styles.text, {
                      [styles.warning]:
                        formData.totalOfRemainingTimeInProject < 0,
                    })}
                  >
                    {toFixed(formData.totalOfRemainingTimeInProject)} h
                  </p>
                ),
                budget_plannedBudget: (
                  <p className={styles.text}>
                    {formData.finalPlannedValueOfProject >= 1000
                      ? `${toFixed(formData.finalPlannedValueOfProject / 1000)}k`
                      : formData.finalPlannedValueOfProject}
                    €
                  </p>
                ),
                budget_realCosts: (
                  <p
                    className={classNames(styles.text, {
                      [styles.warning]:
                        formData.trackingRealCost >
                        formData.finalPlannedValueOfProject,
                    })}
                  >
                    {formData.trackingRealCost >= 1000
                      ? `${toFixed(formData.trackingRealCost / 1000)}k`
                      : formData.trackingRealCost}
                    €
                  </p>
                ),
                budget_acquiredValue: (
                  <p className={styles.text}>
                    {formData.trackingAcquiredValue >= 1000
                      ? `${toFixed(formData.trackingAcquiredValue / 1000)}k`
                      : formData.trackingAcquiredValue}
                    €
                  </p>
                ),
                budget_allocatedBudget: (
                  <p className={styles.text}>
                    {formData.allocatedBudget >= 1000
                      ? `${toFixed(formData.allocatedBudget / 1000)}k`
                      : formData.allocatedBudget}
                    €
                  </p>
                ),
                budget_realMargin: (
                  <p
                    className={classNames(styles.text, {
                      [styles.warning]: formData.realMarginBudget < 0,
                      [styles.positive]: formData.realMarginBudget > 0,
                    })}
                  >
                    {formData.realMarginBudget >= 1000 ||
                    formData.realMarginBudget <= -1000
                      ? `${toFixed(formData.realMarginBudget / 1000)}k`
                      : formData.realMarginBudget}
                    €
                  </p>
                ),
                budget_plannedMargin: (
                  <p
                    className={classNames(styles.text, {
                      [styles.warning]: formData.plannedMarginBudget < 0,
                      [styles.positive]: formData.plannedMarginBudget > 0,
                    })}
                  >
                    {formData.plannedMarginBudget >= 1000 ||
                    formData.plannedMarginBudget <= -1000
                      ? `${toFixed(formData.plannedMarginBudget / 1000)}k`
                      : formData.plannedMarginBudget}
                    €
                  </p>
                ),
                projectManagers: (
                  <div className={styles.tags}>
                    <ResourceList project={formData} />
                  </div>
                ),
              },
            )}
          />
        </div>
      </div>
      {planzone.uuid !== "57ec7e80-dcbb-49af-b11f-665fa6e545c1" && (
        <div
          className={classNames(styles.column, {
            [styles.withZone]: !formData.isTemplate,
          })}
        >
          <div className={styles.tagsGroup}>
            <div className={styles.col}>
              <h3 className={styles.title}>
                {t("projectCreation.statusTitle")}
              </h3>
              <ProjectStatus
                project={formData}
                selectStatus={(status) => editForm({ ...formData, status })}
              />
            </div>
            <div className={styles.formGroup}>
              <div className={styles.row}>
                <div className={styles.dateGroup}>
                  <DateForm
                    startDate={formData.rootList.startDate}
                    endDate={formData.rootList.endDate}
                    setStartDate={(startDate) => {
                      const endDate =
                        !formData.rootList.endDate ||
                        new Date(formData.rootList.endDate) <
                          new Date(startDate)
                          ? startDate
                          : formData.rootList.endDate;
                      editForm({
                        ...formData,
                        rootList: {
                          ...formData.rootList,
                          startDate,
                          endDate,
                        },
                      });
                    }}
                    setEndDate={(endDate) =>
                      editForm({
                        ...formData,
                        rootList: { ...formData.rootList, endDate },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.projectToolsConfiguration}>
            <ProjectToolsConfiguration
              configuration={configuration}
              setConfiguration={setConfiguration}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { ProjectCreationForm };
